import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormModel, FormState} from '../models';
import {FormStateChangedModel} from './form-state-changed.model';
import {MatSelectChange} from '@angular/material/select';
import {FormulaireService} from '../../../services/formulaire/formulaire.service';
import {SubSink} from '@Common/core/utils/subsink';
import {SnackBarToastyService, typeToast} from '@Common/widgets/general/snack-bar';
import {finalize, pairwise, startWith} from 'rxjs/operators';
import {DialogData, DialogValidationComponent} from '@Common/widgets/general/dialog-validation';
import {MatDialog} from '@angular/material/dialog';
import {UntypedFormControl} from '@angular/forms';
import {FormStateOptionsModel} from '../../../services/states/form-state-options.model';
import {StatesFormService} from '../../../services/states/states-form.service';
import {UrlService} from '../../../services/url/url.service';
import {UrlModel} from '../../../services/url/url.model';
import {StateValidationComponent} from './state-validation/state-validation.component';
import {Observable} from 'rxjs';
import {StateValidationModel} from './state-validation/state-validation.model';

@Component({
  selector: 'app-form-states',
  templateUrl: './form-states.component.html',
  styleUrls: ['./form-states.component.scss']
})
export class FormStatesComponent implements OnInit, OnDestroy {
  @Input() form: FormModel;
  @Input() align: 'start' | 'center' | 'end' = 'end';
  @Output() onChangedState: EventEmitter<FormStateChangedModel> = new EventEmitter<FormStateChangedModel>();
  stateCurrent: FormStateOptionsModel;
  previousState: FormStateOptionsModel;
  statesOptions: FormStateOptionsModel[];
  control = new UntypedFormControl();
  onSaving = false;
  subSink = new SubSink();
  constructor(private snakeSvc: SnackBarToastyService,
              private dialog: MatDialog,
              private statesFormSvc: StatesFormService,
              private formSvc: FormulaireService,
              private urlSvc: UrlService) { }

  ngOnInit(): void {
    this._initObserverOldValue();
    this._statesLabel();
  }
  /**
   * pour pouvoir remettre l'état d'avant du formulaire en cas d'annulation de l'action
   */
  private _initObserverOldValue() {
    this.control.valueChanges.pipe(
      startWith([null]),
      pairwise<FormStateOptionsModel>()
    ).subscribe(([old, value]) => {
      // old ne renvoie pas forcément FormStateOptionsModel mais un [] quand aucune valeur n'est fixée (?)
      if ((Array.isArray(old) && ! old[0]) || ! old) {
        this.previousState = value;
      } else {
        this.previousState = old;
      }
    });
  }
  /**
   * Etats suivants possible
   */
  private _statesLabel(state: FormState = this.form?.state) {
    this.statesOptions = this.statesFormSvc.getStateModelsAvailableFormState(state);
    const findState = this.statesOptions.find(_state => _state.state === state);
    if (findState) {
      this.stateCurrent = findState;
    }
  }
  private _generateUrl(form: FormModel): Observable<any> {
    const urlForm = <UrlModel> {
      id: form.url_obj[0]?.id,
      form: form.id,
      description: form.description
    };
    return this.urlSvc.updateOrcreate(urlForm);
  }
  /**
   * Modification de l'état du formulaire
   * Création d'une Url
   * @param nextState
   * @private
   */
  private _updateFormState(nextState: FormState) {
    this.onSaving = true;
    const emitData = <FormStateChangedModel>{
      beforeState: this.form?.state,
      afterState: nextState
    };
    this.form.state = nextState;
    this.subSink.sink = this.formSvc
      .patch(this.form, 'uuid')
      .pipe(finalize(() => this.onSaving = false))
      .subscribe((_form: FormModel) => {
        this._statesLabel(_form.state);
        this.snakeSvc.toasty(
          'Etat du formulaire',
          `L'état du formulaire a bien été passé de "${this.statesFormSvc.getStateLabel(emitData.beforeState)}" à "${this.statesFormSvc.getStateLabel(emitData.afterState)}"`,
          typeToast.success);
        this.onChangedState.emit(emitData);
      });
  }
  private _confirmStatePublish(state: FormState) {
    const dialogRef = this.dialog.open(StateValidationComponent, { data: this.form });
    dialogRef.disableClose = true;
    dialogRef.updatePosition({top: '50px'});
    dialogRef.afterClosed().subscribe((result: StateValidationModel) => {
      if (result.validate) {
        this._updateFormState(state);
      } else {
        this.stateCurrent = this.previousState;
      }
    });
  }
  /**
   * Seul le passage en état publié demande une confirmation
   * @param event
   */
  onSelect(event: MatSelectChange) {
    const nextState = event.value.state;
    if (! this.form.url_obj || this.form.url_obj.length === 0) {
      this.subSink.sink = this._generateUrl(this.form).subscribe(_url => {
        this.form.url_obj = [..._url];
        this._confirmStatePublish(nextState);
      });
    } else {
      if (((this.previousState?.state === FormState.draft || this.previousState?.state === FormState.offline)
          && nextState === FormState.published) ) {
          this._confirmStatePublish(nextState);
        } else {
        this._updateFormState(nextState);
      }
    }
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }
}
